import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { useFormStore } from "../store/formStore";
import { isRequiredDocsVisible, isOwnerEligibilityVisible } from "../hooks/step7Rules";
import { ReviewField } from "./StepReview";
import { buildEntityBlocks, buildOwnershipBlocks, entityHeaderLabel } from "./companyStructureBlocks";
import { MULTI_EMPLOYER_LABELS, PEO_LABELS, isAffiliatedListShown } from "../types/structureConstants";
import { stackedList } from "../utils/reviewHelpers";
import { ownerDisplayName } from "../utils/ownerDisplayName";
import type { Owner } from "../types";
import type { useStepValidation } from "../hooks";

const ownerName = (o: Owner, i: number): string => ownerDisplayName(o, `Owner ${i + 1}`);

interface StepThankYouProps {
  validation: ReturnType<typeof useStepValidation>;
}

// ─── Read-only section wrapper (no edit button) ──────────────────────────────

function ReadOnlySection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-4">
      <div className="bg-brand-green-light border-b border-brand-green px-3 py-2">
        <h3 className="text-xs font-bold text-brand-navy uppercase tracking-wider">{title}</h3>
      </div>
      <div className="py-1">{children}</div>
    </div>
  );
}

// ─── Submitted form summary ───────────────────────────────────────────────────

function SubmittedReview() {
  const data = useFormStore((s) => s.data);
  const mismatches = useFormStore((s) => s.mismatches);
  const uwMethod = useFormStore((s) => s.meta.uwMethod);

  const rf = (label: string, value: React.ComponentProps<typeof ReviewField>["value"], fieldKey?: string, stackOnMobile?: boolean) => (
    <ReviewField label={label} value={value} fieldKey={fieldKey} mismatches={mismatches} stackOnMobile={stackOnMobile} />
  );

  const showRequiredDocs = isRequiredDocsVisible(data.totalEnrolledEmployees, data.replacingMedical);
  const showOwnerEligibility = isOwnerEligibilityVisible(data.ownershipAssignments);
  const isSmallGroup = (data.avgW2Employees20Weeks ?? 0) < 20;
  const showCOBRAFollowup = !isSmallGroup || data.offerCOBRAVoluntary === "Yes";

  return (
    <div>
      {/* Company Information */}
      <ReadOnlySection title="Company Information">
        {rf("Coverage Effective Date", data.effectiveDate)}
        {rf("Company Legal Name", data.companyLegalName)}
        {rf("DBA (Doing Business As)", data.dba || "—")}
        {rf("Employer Federal Tax ID", data.federalTaxId)}
        {rf("Street Address", data.streetAddress)}
        {rf("City", data.city)}
        {rf("State", data.state)}
        {rf("Zip Code", data.zipCode)}
        {rf("Organization Type", data.orgType)}
        {rf("SIC Code", data.sicCode ? `${data.sicCode}${data.sicDescription ? ` — ${data.sicDescription}` : ""}` : "")}
        {rf("Year Business Established", data.yearEstablished)}
        {rf("Avg W-2 Employees (typical business days)", data.avgW2Employees)}
        {rf("Avg W-2 Employees (20+ weeks)", data.avgW2Employees20Weeks)}
      </ReadOnlySection>

      {/* Contacts */}
      <ReadOnlySection title="Contacts">
        {rf("Company Contact – Full Name", data.contactName)}
        {rf("Company Contact – Title", data.contactTitle)}
        {rf("Company Contact – Phone", data.contactPhone)}
        {rf("Company Contact – Email", data.contactEmail, undefined, true)}
        {rf("Fiduciary – Same as Company Contact?", data.fiduciarySameAsContact)}
        {data.fiduciarySameAsContact === "No" && (
          <>
            {rf("Fiduciary – Full Name", data.fiduciaryName)}
            {rf("Fiduciary – Title", data.fiduciaryTitle)}
            {rf("Fiduciary – Phone", data.fiduciaryPhone)}
            {rf("Fiduciary – Email", data.fiduciaryEmail, undefined, true)}
          </>
        )}
        {rf("Need to add an alternate contact?", data.needAlternatePortalAccess)}
        {data.needAlternatePortalAccess === "Yes" && (
          <>
            {rf("Alternate Portal Contact – Name", data.portalContactName)}
            {rf("Alternate Portal Contact – Email", data.portalContactEmail, undefined, true)}
            {rf("Alternate Portal Contact – Phone", data.portalContactPhone)}
            {data.fiduciarySameAsContact === "No" && rf("Who should this person replace?", data.portalReplaces)}
          </>
        )}
      </ReadOnlySection>

      {/* Company Structure — Plan Structure content (ADO-3492 §2). */}
      <ReadOnlySection title="Company Structure">
        {rf("Collective Bargaining (CBA)?", data.hasCba)}
        {data.hasCba === "Yes" && (
          <>
            {rf("CBA Name", data.cbaUnitName)}
            {rf("CBA Effective Date", data.cbaEffectiveDate)}
          </>
        )}
        {rf(
          "PEO / Employee Leasing?",
          // Expand the follow-up peoType code to its readable label (matches StepReview);
          // bare "Yes" would silently drop the "which PEO scenario" detail from the summary.
          data.hasPeo === "Yes" && data.peoType
            ? (PEO_LABELS[data.peoType] ?? "Yes")
            : data.hasPeo,
        )}
        {rf("Multi-Employer Plan?", data.isMultiEmployer)}
        {data.isMultiEmployer === "Yes" && rf(
          "Multi-Employer Type",
          MULTI_EMPLOYER_LABELS[data.multiEmployerType] ?? data.multiEmployerType,
        )}
        {data.isMultiEmployer === "No" && rf("Part of a commonly controlled group?", data.isCommonlyControlledGroup)}
        {/* Plan Structure per-entity blocks — only on the affiliated path (ADO-3492 §2). */}
        {isAffiliatedListShown(data) && buildEntityBlocks(data).map((b) => (
          <div key={b.id}>
            <div className="px-3 pb-1 pt-3">
              <p className="pl-3 text-sm font-semibold text-gray-800 break-words">
                {entityHeaderLabel(b)}
              </p>
            </div>
            <ReviewField label="Federal Tax ID" value={b.taxId} mismatches={mismatches} nested />
            <ReviewField label="Eligible Employees" value={b.employees != null ? String(b.employees) : ""} mismatches={mismatches} nested />
            <ReviewField label="Covered Under Policy" value={b.covered === true ? "Yes" : b.covered === false ? "No" : ""} mismatches={mismatches} nested />
          </div>
        ))}
      </ReadOnlySection>

      {/* Ownership Breakdown (ADO-3492 §3) — its own section on Thank You too,
          per Common Rule (§1): whatever appears on Review must appear here. */}
      <ReadOnlySection title="Ownership Breakdown">
        {(() => {
          const blocks = buildOwnershipBlocks(data);
          const ownerLabelById = new Map(data.owners.map((o, idx) => [o.id, ownerName(o, idx)]));
          return blocks.map((b) => {
            const rows = data.ownershipAssignments.filter((a) => a.entityId === b.id);
            const total = Math.round(rows.reduce((sum, r) => sum + (Number(r.ownership) || 0), 0) * 100) / 100;
            return (
              <div key={b.id}>
                <div className="px-3 pb-1 pt-3">
                  <p className="pl-3 text-sm font-semibold text-gray-800 break-words">
                    {entityHeaderLabel(b)}
                  </p>
                </div>
                <ReviewField
                  label="Total Ownership"
                  value={`${total}%`}
                  mismatches={mismatches}
                  nested
                />
                {rows.map((a) => {
                  const label = ownerLabelById.get(a.ownerId) ?? "Unassigned";
                  const w2 = a.receivingW2Wages === true ? "Yes" : a.receivingW2Wages === false ? "No" : "—";
                  const enr = a.enrollingInBenefits === true ? "Yes" : a.enrollingInBenefits === false ? "No" : "—";
                  return (
                    <ReviewField
                      key={a.id}
                      label={label}
                      value={`${a.ownership || 0}% (W-2: ${w2}, Enrolling: ${enr})`}
                      mismatches={mismatches}
                      nested
                    />
                  );
                })}
              </div>
            );
          });
        })()}
      </ReadOnlySection>

      {/* Coverage & Benefits */}
      <ReadOnlySection title="Coverage &amp; Benefits">
        {rf("Are you offering Optimyl Dental?", data.offeringDental)}
        {rf("Are you offering Optimyl Vision?", data.offeringVision)}
        {rf("Member Deductible Year Type", data.deductibleYearType)}
        {rf("Employer Medical Contribution", data.medicalContribution != null ? `${data.medicalContribution}%` : "")}
        {rf("Will Optimyl replace current group Medical coverage?", data.replacingMedical)}
        {data.replacingMedical === "Yes" && (
          <>
            {rf("Prior Medical Carrier", data.priorMedicalCarrier === "Other" ? (data.priorMedicalCarrierOther || "Other") : data.priorMedicalCarrier)}
            {rf("Prior Medical Plan Type", data.priorMedicalPlanType)}
            {rf("Prior Medical Effective Date", data.priorMedicalEffectiveDate)}
          </>
        )}
        {data.offeringDental === "Yes" && rf("Will Optimyl replace current group Dental coverage?", data.replacingDental)}
        {rf("Reimburse Copays / OOP?", data.reimburseCopays)}
        {rf("Offer HSA?", data.offerHsa)}
        {rf("Offer HRA?", data.offerHra)}
        {rf("Other OOP Reimbursement?", data.otherReimbursement)}
      </ReadOnlySection>

      {/* Eligibility & Elections */}
      <ReadOnlySection title="Eligibility &amp; Elections">
        {rf("Exclude Employee Class?", data.excludeClass)}
        {data.excludeClass === "Yes" && (
          <>
            {rf("Excluded employee class", stackedList(data.excludedClasses))}
            {data.excludedClasses.includes("Other") && rf("Specify excluded class", data.excludedClassesOther)}
          </>
        )}
        {rf("Allow Domestic Partners to be eligible?", data.allowDomesticPartners)}
        {rf("Minimum Hours per Week", data.minHoursPerWeek)}
        {rf("Waiting Period", data.waitingPeriod)}
        {rf("Waive Waiting Period for Current Employees?", data.waiveWaitingPeriod)}
        {data.waiveWaitingPeriod === "No" && rf("Employees in Waiting Period?", data.employeesInWaitingPeriod)}
        {isSmallGroup && rf("Voluntary COBRA?", data.offerCOBRAVoluntary)}
        {showCOBRAFollowup && (
          <>
            {rf("Employees on COBRA?", data.employeesOnCOBRA)}
            {rf("COBRA Admin (Optimyl vs Self)?", data.useOptimylCOBRA === "Yes" ? "Optimyl" : data.useOptimylCOBRA === "No" ? "Self" : "")}
          </>
        )}
        {rf("Want Optimyl-Provided Section 125 Plan?", data.wantOptimylSection125)}
        {data.wantOptimylSection125 === "Yes" && (
          <>
            {rf("Section 125 – Plans to Include", stackedList([
              data.section125PlanMedical && "Medical",
              data.section125PlanDental && "Dental",
              data.section125PlanVision && "Vision",
            ].filter(Boolean) as string[]))}
            {rf("Section 125 – Tiers to Include", stackedList([
              data.section125TierEmployeeOnly && "Employee Only",
              data.section125TierEmployeeSpouse && "Employee & Spouse",
              data.section125TierEmployeeChildren && "Employee & Children",
              data.section125TierEmployeeFamily && "Employee & Family",
            ].filter(Boolean) as string[]))}
          </>
        )}
      </ReadOnlySection>

      {/* Payment */}
      <ReadOnlySection title="Payment">
        {rf("Payment Method", data.paymentMethod)}
        {data.paymentMethod === "ACH" && (
          <>
            {rf("Account Holder Name", data.achAccountHolderName)}
            {rf("Bank Name", data.achBankName)}
            {rf("ACH Routing (ABA) Number", data.achRoutingNumber ? `••••${data.achRoutingNumber.slice(-4)}` : "")}
            {rf("Account Number", data.achAccountNumber ? `••••${data.achAccountNumber.slice(-4)}` : "")}
            {rf("Account Type", data.achAccountType)}
          </>
        )}
        {data.paymentMethod === "Check" && rf("Binder Check", data.documents.binderCheck?.name || "")}
      </ReadOnlySection>

      {/* Documents & Census */}
      <ReadOnlySection title="Documents &amp; Census">
        {showRequiredDocs && (
          <>
            {rf("Employment Verification Document Type", data.docVerificationType)}
            {data.docVerificationType === "State Wage and Tax" && rf("State Wage & Tax Document", data.documents.wageTax?.name || "")}
            {data.docVerificationType === "Payroll Records" && rf("Payroll Records Document", data.documents.payroll?.name || "")}
            {data.docVerificationType === "Business in operation less than 90 days" && rf("New Business Documentation", data.documents.newBusinessDocs?.name || "")}
          </>
        )}
        {data.replacingMedical === "Yes" && rf("Prior Carrier Bill", data.documents.priorBill?.name || "")}
        {showOwnerEligibility && !showRequiredDocs && rf("Company Established Within Last 90 Days?", data.newlyFormedCompany)}
        {data.ownerEligibilityDocs.length > 0 && rf("Owner Eligibility Documents", data.ownerEligibilityDocs.map((d) => d.name).join(", "))}
        {data.documents.censusFile && rf("Employee Census File", data.documents.censusFile.name)}
        {uwMethod !== "Claims" && rf(
          "Census Members",
          data.employees.length > 0
            ? `${data.employees.length} member${data.employees.length !== 1 ? "s" : ""}`
            : "",
        )}
      </ReadOnlySection>
    </div>
  );
}

const TIMELINE_STEPS = [
  {
    title: "Form Review & Document Request",
    body: "Our team will review your intake form. If any additional information is needed, we will reach out to your broker within one to two business days. Once all required information has been received, we will send documents out for signature.",
  },
  {
    title: "Group Review & Issuance — typically 1 to 2 business days after signed documents are received",
    body: "Once signed documents are received, your group will enter formal review. An email confirmation will be sent to your broker once your group has been issued and sent to Allied Benefit Systems for implementation.",
  },
  {
    title: "Welcome Email — 5 to 7 business days after issuance",
    body: "Allied Benefit Systems will send a welcome email with temporary ID cards and plan details. Medical benefits will be active at this point.",
  },
  {
    title: "Pharmacy Benefits — active 4 to 5 business days after welcome email is received",
    body: "Prescription benefits go live 4 to 5 business days after the welcome email has been received.",
  },
  {
    title: "Physical ID Cards — 10 to 14 business days after welcome email",
    body: "Your employees will receive their permanent ID cards in the mail.",
  },
];

// ─── StepThankYou ─────────────────────────────────────────────────────────────

export function StepThankYou({ validation: _validation }: StepThankYouProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="pb-10">
      {/* Hero banner */}
      <div className="rounded-2xl overflow-hidden mb-6 border border-gray-200">
        <div className="bg-brand-navy px-8 py-12 flex flex-col items-center text-center relative overflow-hidden">
          <div className="absolute rounded-full bg-brand-green/10 w-[300px] h-[300px] -top-[120px] -right-[80px]" />
          <div className="absolute rounded-full bg-brand-green/[0.06] w-[220px] h-[220px] -bottom-[90px] -left-[60px]" />
          <img src="/vertical-optimyl-logo-white-text.svg" alt="Optimyl Benefits" className="w-[100px] sm:w-[130px] h-auto mb-5 relative z-10" />
          <h1 className="text-3xl font-black text-white mb-2 leading-tight relative z-10">Thank You for Your Submission</h1>
          <div className="w-14 h-[3px] bg-brand-green rounded-full my-3 relative z-10" />
          <p className="text-xl font-bold text-white/85 mt-2 relative z-10">Your intake form has been received.</p>
        </div>
        <div className="bg-[#f2faf7] border-t-4 border-brand-green px-8 py-5 text-center">
          <p className="text-sm font-extrabold text-brand-navy uppercase tracking-wide">Please review the steps below for what to expect next</p>
        </div>
      </div>

      {/* What to expect timeline */}
      <div className="bg-white rounded-xl border border-gray-200 p-8 mb-6">
        <h2 className="text-lg font-extrabold text-brand-navy mb-5 pb-3 border-b-2 border-brand-green">What to Expect Next</h2>
        <ol className="space-y-0">
          {TIMELINE_STEPS.map((step, i) => (
            <li key={i} className="flex gap-4 py-3.5 border-b border-gray-100 last:border-b-0 text-sm text-gray-700 leading-relaxed">
              <span className="min-w-[32px] h-8 rounded-full bg-brand-navy text-white text-sm font-bold flex items-center justify-center mt-0.5 shrink-0">{i + 1}</span>
              <div>
                <strong className="block text-brand-navy mb-0.5">{step.title}</strong>
                {step.body}
              </div>
            </li>
          ))}
        </ol>
      </div>

      {/* Prescription drug note */}
      <div className="bg-white rounded-xl border border-gray-200 p-8 mb-6">
        <h2 className="text-lg font-extrabold text-brand-navy mb-5 pb-3 border-b-2 border-brand-green">A Note on Prescription Drug Coverage</h2>
        <p className="text-sm text-gray-700 leading-relaxed">To ensure continuity of care during the transition, we recommend communicating to your employees that they fill any needed prescriptions as soon as possible while your new Optimyl Benefits plan is being processed.</p>
      </div>

      {/* How you'll hear from us */}
      <div className="bg-white rounded-xl border border-gray-200 p-8 mb-6">
        <h2 className="text-lg font-extrabold text-brand-navy mb-5 pb-3 border-b-2 border-brand-green">How You Will Hear From Us</h2>
        <p className="text-sm text-gray-700 leading-relaxed">All communication regarding your submission, including any requests for additional information and confirmation of group issuance, will be directed through your broker. The speed of your group's issuance depends on the timely resolution of any open items.</p>
      </div>

      {/* View submitted information collapsible */}
      <div className="bg-white rounded-xl border border-gray-200 p-8 mb-6">
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="w-full flex items-center justify-between mb-5 pb-3 border-b-2 border-brand-green focus:outline-none focus:ring-2 focus:ring-brand-green-50 cursor-pointer"
          aria-expanded={expanded}
        >
          <span className="text-lg font-extrabold text-brand-navy">View Submitted Information</span>
          <ChevronDown size={18} className={`text-gray-400 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} />
        </button>
        {expanded && <SubmittedReview />}
      </div>

      <p className="text-center text-xs text-gray-400 mt-2">We look forward to supporting you and your team.&nbsp;&nbsp;|&nbsp;&nbsp;Optimyl Benefits</p>
    </div>
  );
}
